// Angular imports
import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import {
	Observable,
	Subject,
	catchError,
	map,
	startWith,
	takeUntil,
} from "rxjs";
import {
	AdminViewModel,
	UsuarioActividadProcedureViewModel,
	UsuarioContratoProcedureViewModel,
	UsuarioCentroProcedureViewModel,
	UsuarioZonaProcedureViewModel,
} from "src/app/_core/models/centros/admin.model";
import { EmpleadoViewModel } from "src/app/_core/models/centros/empleado.model";

// Models
import { ContextLocationViewModel } from "src/app/_core/models/main/context-location.model";
import { LinkItem } from "src/app/_core/models/main/link-item.model";
import { getHomeMenu } from "src/app/_core/constants/menu.constants";

// Services
import { Policies } from "src/app/_core/models/main/policies.enum";
import { AdminService } from "src/app/_core/providers/admin.service";
import { AuthService } from "src/app/_core/providers/auth.service";
import {
	GlobalService,
	MigaDePan,
} from "src/app/_core/providers/global.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
	selector: "app-admin-page",
	templateUrl: "./admin.page.html",
	styleUrls: ["./admin.page.scss"],
})
export class AdminPageComponent implements OnInit, OnDestroy {
	// Parameters
	@ViewChild("nombreInput", { static: true }) nombreInput: ElementRef;
	public admin: AdminViewModel;
	public userList: EmpleadoViewModel[] = [];
	public photo: String | ArrayBuffer = "/assets/images/user.png";
	adminFilter: Observable<EmpleadoViewModel[]>;
	adminControl: FormControl = new FormControl("");
	dataSourceUsAc: MatTableDataSource<UsuarioActividadProcedureViewModel>;
	dataSourceUsContrato: MatTableDataSource<UsuarioContratoProcedureViewModel>;
	dataSourceUsZone: MatTableDataSource<UsuarioZonaProcedureViewModel>;
	dataSourceUsCenter: MatTableDataSource<UsuarioCentroProcedureViewModel>;

	// Attributes
	private readonly destroy$ = new Subject<void>();

	// Main
	public contextLocation: ContextLocationViewModel;
	private readonly menu: LinkItem[] = getHomeMenu();

	constructor(
		private readonly _titleService: Title,
		private readonly _translateService: TranslateService,
		private readonly _authService: AuthService,
		private readonly _adminService: AdminService,
		private readonly _masterService: MasterService,
		private readonly _notificationService: NotificationService,
		private readonly _globalService: GlobalService,
	) {
		const miga: MigaDePan[] = [
			{
				nombre: "header.menu.home.admin",
				url: window.location.href,
			},
		];
		this._globalService.limpiarMigas();
		this._globalService.actualizarMigas(miga);
	}

	// Events
	ngOnInit(): void {
		this._globalService.setHeaderOptions(
			[],
			this.contextLocation,
			[],
			this.menu,
		);

		this._titleService.setTitle(
			this._translateService.instant("title.admin.home"),
		);

		this._loadData();

		this.adminFilter = this.adminControl.valueChanges.pipe(
			startWith(""),
			map((value) => (typeof value === "string" ? value : value?.nombre)),
			map((nombre) =>
				nombre ? this._filter(nombre) : this.userList.slice(),
			),
		);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// Private Methods
	private _loadData(): void {
		this._masterService
			.getEmpleados()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.userList = result;
			});
	}

	private _loadAdmin(adminId: number) {
		this._adminService
			.getAdminById(adminId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.PintadoPantalla(result);
			});
	}

	private PintadoPantalla(result: AdminViewModel) {
		this.admin = result;
		this.dataSourceUsAc =
			new MatTableDataSource<UsuarioActividadProcedureViewModel>(
				this.admin.usuarioActividadProcedureViewModel.sort((a, b) =>
					a.actividad.localeCompare(b.actividad),
				),
			);
		this.dataSourceUsContrato =
			new MatTableDataSource<UsuarioContratoProcedureViewModel>(
				(this.admin.usuarioContratoBaseViewModelList ?? []).sort(
					(a, b) =>
						(a.contrato ?? "").localeCompare(b.contrato ?? ""),
				),
			);
		this.dataSourceUsZone =
			new MatTableDataSource<UsuarioZonaProcedureViewModel>(
				this.admin.usuarioZonaProcedureViewModel.sort((a, b) =>
					a.zona.localeCompare(b.zona),
				),
			);
		this.dataSourceUsCenter =
			new MatTableDataSource<UsuarioCentroProcedureViewModel>(
				this.admin.usuarioCentroViewModelList.sort((a, b) =>
					a.centro.localeCompare(b.centro),
				),
			);
	}

	private _filter(nombre: string): EmpleadoViewModel[] {
		const filterValue = nombre.toLowerCase();
		return this.userList.filter((option) =>
			option.nombre.toLowerCase().includes(filterValue),
		);
	}

	private _updateAdmin(admin: AdminViewModel) {
		this._adminService
			.updateAdmin(admin)
			.pipe(takeUntil(this.destroy$))
			.subscribe(
				(result) => {
					//this.admin = result;
					this.PintadoPantalla(result);
					//this._loadAdmin(admin.usuarioId);
					this._notificationService.showSuccess(
						this._translateService.instant(
							"messages.savedSuccessfully",
						),
						null,
					);
				},
				(error) => {
					console.error("Error updating admin:", error);
					this._notificationService.showError(
						this._translateService.instant(
							"messages.errorsWhileSaving",
						),
					);
				},
			);
	}

	private saveFile(response: Blob, userId: number): void {
		const blob = new Blob([response], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `ResumenUsuario_${userId}.xlsx`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	}

	// Public Methods
	exportToExcel(event: Event, userId: number) {
		event.stopPropagation();
		this._adminService
			.exportResumenToExcel(userId)
			.pipe(
				catchError((error) => {
					console.error("Error exporting to Excel", error);
					throw error;
				}),
			)
			.subscribe((response) => {
				this.saveFile(response, userId);
			});
	}

	applyChanges() {
		this.admin.usuarioContratoViewModelList =
			this.admin.usuarioContratoBaseViewModelList;
		this._updateAdmin(this.admin);
		this._loadAdmin(this.admin.usuarioId);
	}

	onReadChange(event: any): void {
		if (!event.checked) {
			this.admin.usuarioAdministradorViewModel.edicionContrato = false;
			this.admin.usuarioAdministradorViewModel.edicionCentro = false;
		}
		this.admin.usuarioAdministradorViewModel.lectura = event.checked;
	}

	onEditContratoChange(event: any): void {
		if (event.checked) {
			this.admin.usuarioAdministradorViewModel.lectura = true;
		}
		this.admin.usuarioAdministradorViewModel.edicionContrato =
			event.checked;
	}

	onEditCenterChange(event: any): void {
		if (event.checked) {
			this.admin.usuarioAdministradorViewModel.lectura = true;
		}
		this.admin.usuarioAdministradorViewModel.edicionCentro = event.checked;
	}

	onEditContratoChangeRow(
		row:
			| UsuarioActividadProcedureViewModel
			| UsuarioZonaProcedureViewModel
			| UsuarioContratoProcedureViewModel,
	): void {
		row.edicion_Contrato = !row.edicion_Contrato;

		if (row.edicion_Contrato) {
			row.lectura = true;
			row.edicion_Centro = true;
		} else {
			row.lectura = false;
			row.edicion_Centro = false;
		}
	}

	onEditCenterChangeRow(
		row:
			| UsuarioActividadProcedureViewModel
			| UsuarioZonaProcedureViewModel
			| UsuarioContratoProcedureViewModel
			| UsuarioCentroProcedureViewModel,
	): void {
		row.edicion_Centro = !row.edicion_Centro;

		if (row.edicion_Centro) {
			row.lectura = true;
		} else {
			row.lectura = false;
		}
	}

	onOptionSelected(event) {
		const admin = event.option.value;
		this.nombreInput.nativeElement.value = admin.nombre;
		this._loadAdmin(admin.id);
		this.getPhoto(admin.email);
	}

	displayFn(admin?: EmpleadoViewModel): string | undefined {
		return admin ? admin.nombre : undefined;
	}

	public getPhoto(email: string): void {
		const unsubscribe$ = new Subject<void>();

		this._authService
			.getUserPhoto(email)
			.pipe(takeUntil(unsubscribe$))
			.subscribe(
				(photoResult) => {
					const reader = new FileReader();
					reader.addEventListener(
						"load",
						() => {
							if (reader && reader.result) {
								this.photo = reader.result;
							} else {
								this.photo = "/assets/images/user.png";
							}
						},
						false,
					);

					if (photoResult) {
						reader.readAsDataURL(photoResult);
					}
				},
				(error) => {
					this.photo = "/assets/images/user.png";
				},
			);

		this.destroy$.pipe(takeUntil(unsubscribe$)).subscribe();
	}

	// On input focus: setup filterPredicate to only filter by input column
	setupFilter(column: string) {
		let dataSource: MatTableDataSource<
			| UsuarioCentroProcedureViewModel
			| UsuarioActividadProcedureViewModel
			| UsuarioContratoProcedureViewModel
			| UsuarioZonaProcedureViewModel
		>;

		switch (column) {
			case "actividad":
				dataSource = this.dataSourceUsAc;

				break;
			case "contrato":
				dataSource = this.dataSourceUsContrato;

				break;
			case "zonaSociedad":
				dataSource = this.dataSourceUsZone;

				break;
			case "centro":
				dataSource = this.dataSourceUsCenter;

				break;

			default:
				break;
		}

		dataSource.filterPredicate = (d, filter: string) => {
			const textToSearch = (d[column] && d[column].toLowerCase()) || "";
			return textToSearch.indexOf(filter) !== -1;
		};
	}

	applyFilter(event: KeyboardEvent, tipoDato: string) {
		const filterValue: string = (event.target as HTMLInputElement).value;

		switch (tipoDato) {
			case "actividad":
				this.dataSourceUsAc.filter = filterValue.trim().toLowerCase();

				break;
			case "contrato":
				this.dataSourceUsContrato.filter = filterValue
					.trim()
					.toLowerCase();

				break;
			case "zonaSociedad":
				this.dataSourceUsZone.filter = filterValue.trim().toLowerCase();

				break;
			case "centro":
				this.dataSourceUsCenter.filter = filterValue
					.trim()
					.toLowerCase();

				break;

			default:
				break;
		}
	}
}
